import { c as _c } from "react/compiler-runtime";
import { getMissingPermissionReason, NO_FREE_SEAT_REASON } from "@components/table/members/assign-seats/assign-seats-utils";
import { SeatCheckbox } from "@components/table/members/assign-seats/seat-checkbox";
import { useSeatsAvailable } from "@components/table/members/assign-seats/use-seats-available";
import { UserAvatarWithMenu } from "@components/user-more-info-menu/user-avatar-with-menu";
import { CheckboxGroup, Text } from "@flat-ui";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { Stack } from "@mui-wrapper";
import { companyMembersSelector } from "@store/members/members-selector";
import { activeSeatUsageSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppSelector } from "@store/store-helper";
import { hasXgSeatPermission } from "@utils/permission-utils";
import { getPrettyName } from "@utils/user-utils";

/** Whether the new project manager needs a Sphere XG seat, and whether they can be given one */

/**
 * Returns whether the new project manager has to be given a Sphere XG seat, which is the case on a
 * plan that limits them (the `ACTIVE_SEAT` constraint) for somebody not holding one yet, such as a
 * new member invited by email.
 *
 * @param selectedMember The new project manager, or the email of the member to invite
 */
export function useManagerSeat(selectedMember) {
  const isActiveSeatPlan = useAppSelector(activeSeatUsageSelector) !== null;
  const workspaceMembers = useAppSelector(companyMembersSelector);
  const {
    canManageXgSeats
  } = useHasUserValidRoleCompanyLevel();
  const seatsAvailable = useSeatsAvailable().xgSeat;
  const blockReason = getBlockReason();
  return {
    isSeatNeeded: isActiveSeatPlan && !!selectedMember && !holdsSeat(),
    option: {
      seat: "xgSeat",
      isHeld: false,
      blockReason,
      isOffered: !blockReason
    },
    seatsAvailable
  };

  /** Whether the selected member holds a Sphere XG seat, as the workspace members tell */
  function holdsSeat() {
    if (typeof selectedMember === "string" || !selectedMember) {
      return false;
    }
    const workspaceMember = workspaceMembers.find(({
      identity
    }) => identity === selectedMember.identity);
    return hasXgSeatPermission({
      member: workspaceMember ?? selectedMember
    });
  }

  /** Returns why the seat cannot be given, or undefined when it can */
  function getBlockReason() {
    if (!canManageXgSeats) {
      return getMissingPermissionReason("xgSeat");
    }
    return seatsAvailable > 0 ? undefined : NO_FREE_SEAT_REASON;
  }
}
/**
 * Offers a Sphere XG seat to the new project manager the same way as the dialog that changes a
 * workspace role, since only members holding one can manage projects.
 */
export function ManagerSeatSection(t0) {
  const $ = _c(25);
  const {
    selectedMember,
    seat,
    isChecked,
    onChange
  } = t0;
  const isEmail = typeof selectedMember === "string";
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: 1
    };
    t2 = <Text variant="bodyM">Only members with a Sphere XG seat can manage projects in this workspace.</Text>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      alignItems: "center",
      gap: 1
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== isEmail || $[4] !== selectedMember) {
    t4 = !isEmail && <UserAvatarWithMenu member={selectedMember} />;
    $[3] = isEmail;
    $[4] = selectedMember;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isEmail || $[7] !== selectedMember) {
    t5 = isEmail ? selectedMember : getPrettyName(selectedMember);
    $[6] = isEmail;
    $[7] = selectedMember;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <strong><var>{t5}</var></strong>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t4 || $[12] !== t6) {
    t7 = <Stack direction="row" component="span" sx={t3}>Assign a seat to{t4}{t6}</Stack>;
    $[11] = t4;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = {
      gap: 1
    };
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = isChecked && !seat.option.blockReason;
  let t10;
  if ($[15] !== onChange) {
    t10 = (_, shouldAssign) => onChange(shouldAssign);
    $[15] = onChange;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== seat.option || $[18] !== seat.seatsAvailable || $[19] !== t10 || $[20] !== t9) {
    t11 = <SeatCheckbox option={seat.option} isChecked={t9} seatsAvailable={seat.seatsAvailable} onChange={t10} />;
    $[17] = seat.option;
    $[18] = seat.seatsAvailable;
    $[19] = t10;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t7) {
    t12 = <Stack sx={t1}>{t2}<CheckboxGroup label={t7} checkboxGroupSx={t8}>{t11}</CheckboxGroup></Stack>;
    $[22] = t11;
    $[23] = t7;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}